import React, { useState } from 'react';
import { PageVariant, Language } from '../../types';
import { TRANSLATIONS } from '../../translations';
import { IntakeModal } from './IntakeModal';
import { 
  ArrowLeft, 
  ChevronRight, 
  Building2, 
  PieChart, 
  HardHat, 
  Compass, 
  CheckCircle2, 
  FileText, 
  Link as LinkIcon, 
  Users, 
  Briefcase, 
  ShieldCheck, 
  Layers, 
  ArrowUpRight,
  Sparkles
} from 'lucide-react';

export type PathwayKey = 'propertySites' | 'investmentCapital' | 'projectsDelivery' | 'professionalExpertise';

interface Props {
  pathwayKey: PathwayKey;
  language?: Language;
  lang?: Language;
  setVariant?: (variant: PageVariant) => void;
  onNavigate?: (variant: PageVariant) => void;
}

const pathwayVariantMap: Record<PathwayKey, { variant: PageVariant; param: string }> = {
  propertySites: { variant: PageVariant.EXPLORE_PROPERTY_SITES, param: 'explore-property-sites' },
  investmentCapital: { variant: PageVariant.EXPLORE_INVESTMENT_CAPITAL, param: 'explore-investment-capital' },
  projectsDelivery: { variant: PageVariant.EXPLORE_PROJECTS_DELIVERY, param: 'explore-projects-delivery' },
  professionalExpertise: { variant: PageVariant.EXPLORE_PROFESSIONAL_EXPERTISE, param: 'explore-professional-expertise' }
};

const pathwayCategoryMap: Record<PathwayKey, string> = {
  propertySites: 'Property & Sites',
  investmentCapital: 'Investment & Capital',
  projectsDelivery: 'Projects & Delivery',
  professionalExpertise: 'Professional Expertise'
};

const pathwayIconsMap: Record<PathwayKey, React.ReactNode> = {
  propertySites: <Building2 className="w-6 h-6" />,
  investmentCapital: <PieChart className="w-6 h-6" />,
  projectsDelivery: <HardHat className="w-6 h-6" />,
  professionalExpertise: <Compass className="w-6 h-6" />
};

export const PathwayDetailPage: React.FC<Props> = ({
  pathwayKey,
  language: propLanguage,
  lang,
  setVariant,
  onNavigate
}) => {
  const activeLanguage = propLanguage || lang || Language.EN;
  const [isIntakeOpen, setIsIntakeOpen] = useState(false);

  const eoTrans = TRANSLATIONS[activeLanguage]?.exploreOpportunities || TRANSLATIONS[Language.EN].exploreOpportunities;
  const detailTrans = (eoTrans as any)?.pathwayDetails?.[pathwayKey] || (TRANSLATIONS[Language.EN].exploreOpportunities as any)?.pathwayDetails?.[pathwayKey];

  const handleNavigate = (targetVariant: PageVariant, param: string) => {
    if (setVariant) setVariant(targetVariant);
    else if (onNavigate) onNavigate(targetVariant);
    window.history.pushState({}, '', `?v=${param}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToMain = () => {
    if (setVariant) setVariant(PageVariant.EXPLORE_OPPORTUNITIES);
    else if (onNavigate) onNavigate(PageVariant.EXPLORE_OPPORTUNITIES);
    window.history.pushState({}, '', '?v=explore-opportunities');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pathwayNavItems: { key: PathwayKey; label: string }[] = [
    { key: 'propertySites', label: eoTrans?.pathways?.items?.[0]?.title || 'Property & Sites' },
    { key: 'investmentCapital', label: eoTrans?.pathways?.items?.[1]?.title || 'Investment & Capital' },
    { key: 'projectsDelivery', label: eoTrans?.pathways?.items?.[2]?.title || 'Projects & Delivery' },
    { key: 'professionalExpertise', label: eoTrans?.pathways?.items?.[3]?.title || 'Professional Expertise' }
  ];

  if (!detailTrans) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
        <p className="text-slate-600">Loading pathway details...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 selection:bg-blue-100">
      {/* Top Breadcrumb & Pathway Switcher Bar */}
      <div className="bg-slate-900 text-white border-b border-slate-800 sticky top-0 z-30 shadow-md">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex flex-col md:flex-row items-center justify-between gap-4">
          <button
            onClick={handleBackToMain}
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-300 hover:text-white transition-colors group"
          >
            <ArrowLeft size={16} className="transform group-hover:-translate-x-1 transition-transform" />
            <span>{detailTrans.backToAll || 'Back to All Opportunities'}</span>
          </button>

          {/* Quick Pathway Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-none">
            {pathwayNavItems.map((item) => {
              const isActive = item.key === pathwayKey;
              return (
                <button
                  key={item.key}
                  onClick={() => handleNavigate(pathwayVariantMap[item.key].variant, pathwayVariantMap[item.key].param)}
                  className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap transition-all rounded-none ${
                    isActive
                      ? 'bg-blue-600 text-white border-b-2 border-white'
                      : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20 px-6 md:px-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:24px_24px] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 bg-blue-950/80 border border-blue-500/30 text-blue-300 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] mb-6">
            <Sparkles size={14} className="text-blue-400" />
            <span>{detailTrans.badge || 'LE SECTOR ECOSYSTEM PATHWAY'}</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-heading uppercase tracking-widest leading-tight text-white mb-6 max-w-4xl">
            {detailTrans.headline}
          </h1>

          <p className="text-lg md:text-xl text-slate-300 max-w-3xl leading-relaxed font-light mb-10">
            {detailTrans.subheadline}
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <button
              onClick={() => setIsIntakeOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-widest text-xs px-8 py-4 transition-all shadow-lg hover:shadow-blue-600/30 flex items-center justify-center gap-2"
            >
              <span>{detailTrans.heroCta || 'Enquire About This Pathway'}</span>
              <ChevronRight size={16} />
            </button>
            <button
              onClick={handleBackToMain}
              className="border border-slate-700 hover:border-slate-500 text-slate-300 hover:text-white font-bold uppercase tracking-widest text-xs px-8 py-4 transition-all flex items-center justify-center gap-2"
            >
              <span>{detailTrans.secondaryCta || 'View Opportunity Directory'}</span>
            </button>
          </div>

          {/* Quick Highlight Badges */}
          {detailTrans.highlights && detailTrans.highlights.length > 0 && (
            <div className="mt-14 pt-8 border-t border-slate-800/80 grid grid-cols-2 md:grid-cols-4 gap-4">
              {detailTrans.highlights.map((h: string, idx: number) => (
                <div key={idx} className="flex items-center gap-3 text-xs font-semibold text-slate-300">
                  <CheckCircle2 size={16} className="text-blue-400 shrink-0" />
                  <span>{h}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-6 md:px-12 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-blue-600 mb-3">
                {detailTrans.overviewTagline || 'Ecosystem Foundation'}
              </h2>
              <h3 className="text-3xl md:text-4xl font-heading uppercase tracking-widest text-slate-900 mb-6">
                {detailTrans.overviewTitle || 'What This Pathway Is About'}
              </h3>
              <p className="text-base md:text-lg text-slate-700 leading-relaxed mb-6 font-normal">
                {detailTrans.overviewDesc}
              </p>
              {detailTrans.overviewSubText && (
                <p className="text-sm text-slate-600 leading-relaxed font-normal">
                  {detailTrans.overviewSubText}
                </p>
              )}
            </div>

            <div className="lg:col-span-5 bg-slate-900 text-white p-8 md:p-10 border border-slate-800 shadow-xl relative">
              <div className="w-12 h-12 bg-blue-600 text-white flex items-center justify-center mb-6">
                {pathwayIconsMap[pathwayKey]}
              </div>
              <h4 className="text-xl font-heading uppercase tracking-wider mb-4 text-white">
                {detailTrans.cardTitle || 'Facilitated Connection'}
              </h4>
              <p className="text-sm text-slate-300 leading-relaxed font-light mb-6">
                {detailTrans.cardDesc || 'Le Sector coordinates and structures connections discreetly to ensure alignability before direct party engagements.'}
              </p>
              <div className="pt-4 border-t border-slate-800 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-400">
                <ShieldCheck size={16} />
                <span>Confidential & Structured Process</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section (Who This Is For) */}
      {detailTrans.whoIsItForItems && detailTrans.whoIsItForItems.length > 0 && (
        <section className="py-20 px-6 md:px-12 bg-slate-50 border-b border-slate-200">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-3">
                {detailTrans.whoTagline || 'Target Participants'}
              </h2>
              <h3 className="text-3xl md:text-4xl font-heading uppercase tracking-widest text-slate-900">
                {detailTrans.whoIsItForTitle || 'Who This Is For'}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {detailTrans.whoIsItForItems.map((item: any, idx: number) => (
                <div key={idx} className="bg-white p-8 border border-slate-200 shadow-sm hover:border-slate-400 transition-all">
                  <div className="w-10 h-10 bg-slate-100 text-slate-800 flex items-center justify-center mb-6">
                    <Users size={20} />
                  </div>
                  <h4 className="text-lg font-heading uppercase tracking-wider text-slate-900 mb-3">
                    {item.title}
                  </h4>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Opportunity Types Section */}
      {detailTrans.opportunityTypesItems && detailTrans.opportunityTypesItems.length > 0 && (
        <section className="py-20 px-6 md:px-12 bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-blue-600 mb-3">
                {detailTrans.typesTagline || 'Ecosystem Scope'}
              </h2>
              <h3 className="text-3xl md:text-4xl font-heading uppercase tracking-widest text-slate-900">
                {detailTrans.opportunityTypesTitle || 'What Opportunities May Exist'}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {detailTrans.opportunityTypesItems.map((opp: any, idx: number) => (
                <div key={idx} className="bg-slate-50 p-8 border border-slate-200 hover:bg-white hover:border-slate-400 transition-all group">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-slate-900 text-white px-2.5 py-1">
                      {opp.tag || 'Opportunity Scope'}
                    </span>
                    <Briefcase size={18} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                  </div>
                  <h4 className="text-xl font-heading uppercase tracking-wider text-slate-900 mb-3">
                    {opp.title}
                  </h4>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {opp.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How Le Sector Facilitates */}
      {detailTrans.facilitationSteps && detailTrans.facilitationSteps.length > 0 && (
        <section className="py-20 px-6 md:px-12 bg-slate-900 text-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-blue-400 mb-3">
                {detailTrans.facTagline || 'Facilitated Execution'}
              </h2>
              <h3 className="text-3xl md:text-4xl font-heading uppercase tracking-widest text-white">
                {detailTrans.facilitationTitle || 'How Le Sector Facilitates'}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {detailTrans.facilitationSteps.map((step: any, idx: number) => (
                <div key={idx} className="bg-slate-800/80 p-8 border border-slate-700/60 relative flex flex-col justify-between">
                  <div>
                    <span className="text-3xl font-heading text-blue-400 mb-4 block">
                      {step.step || `0${idx + 1}`}
                    </span>
                    <h4 className="text-lg font-heading uppercase tracking-wider text-white mb-3">
                      {step.title}
                    </h4>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Example Scenarios Section */}
      {detailTrans.scenariosItems && detailTrans.scenariosItems.length > 0 && (
        <section className="py-20 px-6 md:px-12 bg-slate-50 border-b border-slate-200">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-3">
                {detailTrans.scenariosTagline || 'Practical Applications'}
              </h2>
              <h3 className="text-3xl md:text-4xl font-heading uppercase tracking-widest text-slate-900">
                {detailTrans.scenariosTitle || 'Example Project Scenarios'}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {detailTrans.scenariosItems.map((sc: any, idx: number) => (
                <div key={idx} className="bg-white p-8 border border-slate-200 shadow-sm flex flex-col justify-between">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 block mb-2">
                      {sc.type || 'Scenario'}
                    </span>
                    <h4 className="text-lg font-heading uppercase tracking-wider text-slate-900 mb-4">
                      {sc.title}
                    </h4>
                    <p className="text-xs text-slate-600 leading-relaxed mb-6 font-normal">
                      {sc.description}
                    </p>
                  </div>
                  <div className="pt-4 border-t border-slate-100">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1 font-bold">
                      Target Match:
                    </span>
                    <span className="text-xs font-bold text-slate-800">
                      {sc.targetMatch}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Future Resources & Reference Link Architecture Section */}
      <section className="py-20 px-6 md:px-12 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-blue-600 mb-3">
              {detailTrans.resourcesTagline || 'Information & References'}
            </h2>
            <h3 className="text-3xl md:text-4xl font-heading uppercase tracking-widest text-slate-900 mb-4">
              {detailTrans.futureResourcesTitle || 'Resources & Project Information'}
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed font-normal">
              {detailTrans.futureResourcesDesc || 'Access site planning guides, project briefs, and related technical frameworks.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(detailTrans.futureResourcesItems || []).map((res: any, idx: number) => (
              <div key={idx} className="bg-slate-50 p-8 border border-slate-200 flex flex-col justify-between group hover:border-slate-400 transition-all">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center">
                      <FileText size={18} />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 bg-slate-200 text-slate-700">
                      {res.status || 'Slot / Ready'}
                    </span>
                  </div>
                  <h4 className="text-base font-heading uppercase tracking-wider text-slate-900 mb-2">
                    {res.title}
                  </h4>
                  <p className="text-xs text-slate-600 leading-relaxed mb-6">
                    {res.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-wider group-hover:text-blue-600 transition-colors flex items-center gap-1.5">
                    <LinkIcon size={14} />
                    {res.linkText || 'Request Resource'}
                  </span>
                  <ArrowUpRight size={16} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-24 px-6 md:px-12 bg-slate-900 text-white text-center relative overflow-hidden">
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-5xl font-heading uppercase tracking-widest text-white mb-6">
            {detailTrans.ctaBox?.title || 'Have an Opportunity or Enquiry?'}
          </h2>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed font-light mb-10 max-w-2xl mx-auto">
            {detailTrans.ctaBox?.desc || 'Submit your inquiry to Le Sector. Our team evaluates alignments and coordinates confidential introductions.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => setIsIntakeOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-widest text-xs px-10 py-5 transition-all shadow-xl hover:shadow-blue-600/40"
            >
              {detailTrans.ctaBox?.buttonText || 'Submit Pathway Enquiry'}
            </button>
            <button
              onClick={handleBackToMain}
              className="border border-slate-700 hover:border-slate-500 text-slate-300 hover:text-white font-bold uppercase tracking-widest text-xs px-10 py-5 transition-all"
            >
              {detailTrans.backToAll || 'Back to Opportunities'}
            </button>
          </div>
        </div>
      </section>

      {/* Intake Modal */}
      <IntakeModal
        isOpen={isIntakeOpen}
        onClose={() => setIsIntakeOpen(false)}
        language={activeLanguage}
        prefillCategory={pathwayCategoryMap[pathwayKey]}
      />
    </div>
  );
};
